import "./styles/app.css";
import { initAppStorage } from "./bootstrap.js";
initAppStorage();

import "./tabs/chat-tab.js";
import "./tabs/cowork-tab.js";
import "./tools/markdown-fixups.js";
import "./tools/eml-attachments.js";
import "./tools/model-menu.js";

import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { keyed } from "lit/directives/keyed.js";

/**
 * Global 401 watcher (issue #6). When the session cookie expires the app used
 * to keep accepting input while every API call silently failed — it "felt
 * buggy". Patching fetch lets us catch the first 401 from any /api/ call and
 * flip the app to the login screen with a clear message instead. Local data
 * (IndexedDB sessions, drafts) is untouched by this — after re-login
 * everything is still there.
 */
const SESSION_EXPIRED_EVENT = "jtm:session-expired";
const originalFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
	const res = await originalFetch(input, init);
	if (res.status === 401) {
		const url =
			typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
		// /api/me 401s are the normal signed-out probe — not an expiry signal.
		if (url.includes("/api/") && !url.includes("/api/me")) {
			window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
		}
	}
	return res;
};

type Tab = "chat" | "cowork" | "tenders";

interface SessionUser {
	id: string;
	email: string;
	displayName: string;
}

type AuthState =
	| { kind: "loading" }
	| { kind: "anonymous"; msAuthEnabled: boolean }
	| { kind: "authenticated"; user: SessionUser };

@customElement("app-shell")
class AppShell extends LitElement {
	createRenderRoot() {
		return this;
	}

	@state() private active: Tab = "chat";
	@state() private auth: AuthState = { kind: "loading" };
	@state() private model = "";
	@state() private sessionExpired = false;
	/** True when the signed-in user is on the TENDER_USERS list — gates the
	 *  Tenders tab (issue #5). */
	@state() private tenders = false;

	private sessionCheckTimer?: ReturnType<typeof setInterval>;
	private onSessionExpired = () => void this.checkSession();

	async connectedCallback() {
		super.connectedCallback();
		window.addEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired);
		// Belt-and-braces: also re-validate the session periodically so an
		// expiry surfaces even while the user is only using the Chat tab
		// (whose LLM traffic doesn't hit authenticated routes).
		this.sessionCheckTimer = setInterval(() => void this.checkSession(), 5 * 60 * 1000);
		await Promise.all([this.loadMe(), this.loadHealth()]);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		window.removeEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired);
		if (this.sessionCheckTimer) clearInterval(this.sessionCheckTimer);
	}

	/** Re-probe /api/me; only flip to the login screen on a definite 401 —
	 *  transient network errors must not kick a working user out of the UI. */
	private async checkSession() {
		if (this.auth.kind !== "authenticated") return;
		try {
			const res = await originalFetch("/api/me");
			if (res.status === 401) {
				const body = (await res.json().catch(() => ({}))) as { msAuthEnabled?: boolean };
				this.sessionExpired = true;
				this.auth = { kind: "anonymous", msAuthEnabled: body.msAuthEnabled ?? true };
			}
		} catch {
			/* offline / transient — keep the workspace up */
		}
	}

	private async loadMe() {
		try {
			const res = await fetch("/api/me");
			if (res.ok) {
				const body = (await res.json()) as { user: SessionUser; tenders?: boolean };
				this.auth = { kind: "authenticated", user: body.user };
				this.tenders = body.tenders ?? false;
				this.sessionExpired = false;
				return;
			}
			if (res.status === 401) {
				const body = (await res.json().catch(() => ({}))) as { msAuthEnabled?: boolean };
				this.auth = { kind: "anonymous", msAuthEnabled: body.msAuthEnabled ?? true };
				return;
			}
			this.auth = { kind: "anonymous", msAuthEnabled: true };
		} catch {
			this.auth = { kind: "anonymous", msAuthEnabled: true };
		}
	}

	private async loadHealth() {
		try {
			const res = await fetch("/api/health");
			if (res.ok) {
				const body = (await res.json()) as { model: string };
				this.model = body.model;
			}
		} catch {
			/* ignore */
		}
	}

	private async logout() {
		await fetch("/auth/logout", { method: "POST" });
		this.sessionExpired = false;
		this.auth = { kind: "anonymous", msAuthEnabled: true };
	}

	render() {
		if (this.auth.kind === "loading") {
			return html`<div class="flex items-center justify-center h-full text-muted-foreground">…</div>`;
		}
		if (this.auth.kind === "anonymous") {
			return this.renderLogin(this.auth.msAuthEnabled);
		}
		return this.renderWorkspace(this.auth.user);
	}

	private renderLogin(msAuthEnabled: boolean) {
		return html`
			<div class="flex flex-1 items-center justify-center px-6">
				<div
					class="w-full flex flex-col items-center gap-6 py-12"
					style="max-width: 22rem"
				>
					<img
						src="/jtm-logo.png"
						alt="JTM digital"
						class="jtm-logo"
						style="height: 56px"
						width="200"
						height="94"
					/>
					<div class="text-center flex flex-col gap-2">
						<div class="jtm-eyebrow">pi workspace</div>
						${this.sessionExpired
							? html`<div
									class="text-sm rounded border border-border bg-secondary text-secondary-foreground px-3 py-2"
								>
									Deine Sitzung ist abgelaufen — bitte melde dich erneut an. Deine
									Chats und Tasks bleiben erhalten.
								</div>`
							: ""}
						<h1 class="font-display text-2xl text-foreground">Sign in to continue</h1>
						<p class="text-sm text-muted-foreground">
							${msAuthEnabled
								? "Use your Microsoft work account."
								: "Microsoft sign-in is not configured on this deployment."}
						</p>
					</div>
					${msAuthEnabled
						? html`
								<a
									href="/auth/login"
									class="w-full text-center rounded-full bg-primary text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
									style="padding: 0.75rem 1rem"
								>
									Sign in with Microsoft
								</a>
							`
						: html`
								<div class="text-xs text-muted-foreground text-center">
									Set <code class="text-foreground">AZURE_TENANT_ID</code>,
									<code class="text-foreground">AZURE_CLIENT_ID</code>, and
									<code class="text-foreground">AZURE_CLIENT_SECRET</code> in your
									<code class="text-foreground">.env</code> to enable sign-in.
								</div>
							`}
				</div>
			</div>
		`;
	}

	private renderWorkspace(user: SessionUser) {
		return html`
			<header class="border-b border-border bg-background px-6 py-4 flex items-center gap-8">
				<img
					src="/jtm-logo.png"
					alt="JTM digital"
					class="jtm-logo"
					width="200"
					height="94"
				/>
				<nav class="flex gap-6">
					${this.tabButton("chat", "Chat")} ${this.tabButton("cowork", "Cowork")}
					${this.tenders ? this.tabButton("tenders", "Tenders") : ""}
				</nav>
				<span class="jtm-tagline hidden md:inline">pi workspace</span>
				<div class="ml-auto text-xs text-muted-foreground flex gap-4 items-center">
					${this.model
						? html`<span title="LLM" class="flex items-center gap-1.5">
								<span class="inline-block w-1.5 h-1.5 rounded-full bg-accent"></span>
								${this.model}
							</span>`
						: ""}
					<span title=${user.email} class="text-foreground">${user.displayName}</span>
					<button
						class="text-muted-foreground hover:text-foreground cursor-pointer"
						title="Sign out"
						@click=${() => this.logout()}
					>
						Sign out
					</button>
				</div>
			</header>
			<main class="flex flex-1 min-h-0">
				${this.active === "chat"
					? html`<chat-tab></chat-tab>`
					: keyed(
							this.active,
							html`<cowork-tab scope=${this.active === "tenders" ? "tenders" : "personal"}></cowork-tab>`,
						)}
			</main>
		`;
	}

	private tabButton(tab: Tab, label: string) {
		const active = this.active === tab;
		return html`
			<button
				class="jtm-tab cursor-pointer"
				data-active=${active ? "true" : "false"}
				@click=${() => (this.active = tab)}
			>
				${label}
			</button>
		`;
	}
}

const root = document.getElementById("app");
if (root) root.appendChild(document.createElement("app-shell"));

declare global {
	interface HTMLElementTagNameMap {
		"app-shell": AppShell;
	}
}
